<script lang="ts">
    import { onMount, afterUpdate } from 'svelte';
    import { removeToast } from '../stores/toast';
    import type { Toast } from '../stores/toast';
    import Icon from './Icon.svelte';
    import type { IconName } from './Icon.svelte';

    export let toast: Toast;

    let visible = false;
    let progressWidth = 100;
    let animFrame: number;
    let startTime: number;
    let activeDuration = 0;

    const ICON_MAP: Record<Toast['type'], IconName> = {
        info:    'info',
        success: 'success',
        warning: 'warning',
        error:   'error',
    };

    onMount(() => {
        requestAnimationFrame(() => { visible = true; });

        if (toast.duration > 0) {
            startCountdown(toast.duration);
        }

        return () => cancelAnimationFrame(animFrame);
    });

    afterUpdate(() => {
        // Toast was updated from sticky (0) to timed — start the countdown
        if (toast.duration > 0 && activeDuration === 0) {
            progressWidth = 100;
            startCountdown(toast.duration);
            setTimeout(() => dismiss(), toast.duration);
        }
    });

    function startCountdown(duration: number) {
        activeDuration = duration;
        startTime = performance.now();
        cancelAnimationFrame(animFrame);
        tick();
    }

    function tick() {
        animFrame = requestAnimationFrame((now) => {
            const elapsed = now - startTime;
            progressWidth = Math.max(0, 100 - (elapsed / activeDuration) * 100);
            if (progressWidth > 0) tick();
        });
    }

    function dismiss() {
        visible = false;
        setTimeout(() => removeToast(toast.id), 250);
    }

    function handleClick() {
        if (toast.onClick) {
            toast.onClick();
            dismiss();
        }
    }

    $: tag = toast.onClick ? 'button' : 'div';
</script>

<svelte:element
    this={tag}
    class="toast toast--{toast.type}"
    class:toast--visible={visible}
    class:toast--clickable={!!toast.onClick}
    role={toast.onClick ? undefined : 'alert'}
    aria-live="assertive"
    on:click={handleClick}
>
    <span class="toast__icon"><Icon name={ICON_MAP[toast.type]} size={20} /></span>
    <div class="toast__body">
        {#if toast.title}
            <p class="toast__title">{toast.title}</p>
        {/if}
        <p class="toast__message">{toast.message}</p>
    </div>
    {#if toast.dismissible}
        <button class="toast__close" aria-label="Dismiss" on:click|stopPropagation={dismiss}>✕</button>
    {/if}    {#if toast.progress === 'indeterminate'}
        <div class="toast__progress toast__progress--indeterminate"></div>
    {:else if typeof toast.progress === 'number'}
        <div class="toast__progress" style="width: {toast.progress}%"></div>
    {:else if toast.duration > 0}
        <div class="toast__progress" style="width: {progressWidth}%"></div>
    {/if}
</svelte:element>
